const indexTombstones = (tombstones) => {

    const byUid = {};
    const byFp = {};

    tombstones.forEach((t) => {
        if (!byUid[t.uid] || t.deletedAt > byUid[t.uid].deletedAt) byUid[t.uid] = t;
        if (t.fingerprint && (!byFp[t.fingerprint] || t.deletedAt > byFp[t.fingerprint].deletedAt)) byFp[t.fingerprint] = t;
    });

    return { byUid, byFp };

};

const newestTombstone = (record, byUid, byFp) => {

    const a = byUid[record.uid];
    const b = byFp[record.fingerprint];

    if (a && b) return a.deletedAt >= b.deletedAt ? a : b;

    return a || b || null;

};

/**
 * A record is deleted if a tombstone for its uid is at least as new as the record. Same uid only, on purpose:
 * a tombstone for the same URL under another uid must not delete a playlist the user added again afterwards.
 */
export const applyTombstones = (records, tombstones) => {

    const { byUid } = indexTombstones(tombstones);
    const survivors = [];
    const removed = [];

    records.forEach((record) => {
        const tomb = byUid[record.uid];
        if (tomb && tomb.deletedAt >= record.updatedAt) removed.push(record.uid);
        else survivors.push(record);
    });

    return { survivors, removed };

};

/**
 * Merges `incoming` into `target` and returns the new list. Rules, in order:
 *  1. A deletion at least as new as the incoming record wins, so an old source can't resurrect a deleted
 *     playlist. That is a tombstone for the same uid, or for the same fingerprint (a copy deleted earlier).
 *     A record edited or re-created AFTER the deletion is newer and comes through.
 *  2. Same uid: the newer `updatedAt` wins; a tie keeps the target's record.
 *  3. New uid, but its fingerprint is already in the target: the same playlist, skipped.
 * The target's order is kept; new records are appended.
 *
 * `allowIncomingDuplicates`: imports of old data keep every entry the user had, even two with the same
 * fingerprint (the old backend allowed that). Duplicates of what is ALREADY in the target are still skipped.
 */
export const mergeIntoTarget = (target, incoming, tombstones, options = {}) => {

    const stats = { added: 0, updated: 0, keptTarget: 0, skippedTombstoned: 0, skippedDuplicate: 0 };
    const { byUid, byFp } = indexTombstones(tombstones);

    const records = target.slice();
    const indexByUid = {};
    records.forEach((r, i) => { indexByUid[r.uid] = i; });

    const targetFingerprints = {};
    target.forEach((r) => { targetFingerprints[r.fingerprint] = true; });
    const addedFingerprints = {};

    incoming.forEach((record) => {

        const tomb = newestTombstone(record, byUid, byFp);

        if (tomb && tomb.deletedAt >= record.updatedAt) {
            stats.skippedTombstoned++;
            return;
        }

        const at = indexByUid[record.uid];

        if (at !== undefined) {
            if (record.updatedAt > records[at].updatedAt) {
                records[at] = record;
                stats.updated++;
            } else {
                stats.keptTarget++;
            }
            return;
        }

        if (targetFingerprints[record.fingerprint] || (!options.allowIncomingDuplicates && addedFingerprints[record.fingerprint])) {
            stats.skippedDuplicate++;
            return;
        }

        indexByUid[record.uid] = records.length;
        records.push(record);
        addedFingerprints[record.fingerprint] = true;
        stats.added++;

    });

    return { records, stats };

};
